import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

interface DragState {
  activeId: string;
  order: string[];
  offsetY: number;
  offsetX: number;
}

export function useDragReorder(ids: string[], onCommit: (ids: string[]) => void,
  { layout = 'list' }: { layout?: 'list' | 'grid' } = {}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const commit = useRef(onCommit);
  commit.current = onCommit;
  const session = useRef<{
    id: string; pointerId: number; startX: number; startY: number; x: number; y: number;
    original: string[]; order: string[]; active: boolean; target: HTMLElement;
    scrollParent: HTMLElement | null;
  } | null>(null);
  const hold = useRef<number | null>(null);
  const scrollFrame = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (hold.current !== null) window.clearTimeout(hold.current);
    if (scrollFrame.current !== null) window.cancelAnimationFrame(scrollFrame.current);
    hold.current = scrollFrame.current = null;
    const current = session.current;
    session.current = null;
    if (current?.target.hasPointerCapture?.(current.pointerId))
      current.target.releasePointerCapture(current.pointerId);
    setDrag(null);
  }, []);

  const registerItem = useCallback((id: string, element: HTMLElement | null) => {
    if (element) itemRefs.current.set(id, element);
    else itemRefs.current.delete(id);
  }, []);

  const update = useCallback(() => {
    const current = session.current;
    if (!current?.active) return;
    const offsetY = current.y - current.startY;
    const offsetX = current.x - current.startX;
    let order: string[];
    if (layout === 'grid') {
      let nearest = current.id;
      let distance = Infinity;
      for (const id of current.order) {
        const element = itemRefs.current.get(id);
        if (!element || element.hidden) continue;
        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        if (current.x >= rect.left && current.x <= rect.right && current.y >= rect.top && current.y <= rect.bottom) {
          nearest = id;
          break;
        }
        const next = Math.hypot(current.x - (rect.left + rect.width / 2), current.y - (rect.top + rect.height / 2));
        if (next < distance) { distance = next; nearest = id; }
      }
      const to = current.order.indexOf(nearest);
      order = current.order.filter(id => id !== current.id);
      order.splice(to, 0, current.id);
    } else {
      const height = itemRefs.current.get(current.id)?.getBoundingClientRect().height || 56;
      const from = current.original.indexOf(current.id);
      const to = Math.min(Math.max(from + Math.round(offsetY / (height + 8)), 0), current.original.length - 1);
      order = current.original.filter(id => id !== current.id);
      order.splice(to, 0, current.id);
    }
    current.order = order;
    setDrag({ activeId: current.id, order, offsetY, offsetX });
  }, [layout]);

  const autoScroll = useCallback(function scroll() {
    const current = session.current;
    if (!current?.active) return;
    const rect = current.scrollParent?.getBoundingClientRect();
    const top = Math.max(0, rect?.top ?? 0);
    const navigation = document.querySelector('.mobile-nav')?.getBoundingClientRect();
    const viewportBottom = navigation && navigation.height > 0 ? navigation.top : window.innerHeight;
    const bottom = Math.min(viewportBottom, rect?.bottom ?? viewportBottom);
    const delta = current.y < top + 64 ? -12 : current.y > bottom - 64 ? 12 : 0;
    if (delta) {
      if (current.scrollParent) current.scrollParent.scrollTop += delta;
      else window.scrollBy(0, delta);
      update();
    }
    scrollFrame.current = window.requestAnimationFrame(scroll);
  }, [update]);

  const beginDrag = useCallback((id: string, event: ReactPointerEvent) => {
    if (event.isPrimary === false || event.button !== 0 || !ids.includes(id) || session.current) return;
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    let scrollParent: HTMLElement | null = target.parentElement;
    while (scrollParent && !(scrollParent.scrollHeight > scrollParent.clientHeight &&
      /auto|scroll/.test(getComputedStyle(scrollParent).overflowY))) scrollParent = scrollParent.parentElement;
    session.current = {
      id, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      x: event.clientX, y: event.clientY, original: [...ids], order: [...ids], active: false,
      target, scrollParent,
    };
    const activate = () => {
      hold.current = null;
      if (!session.current) return;
      session.current.active = true;
      target.setPointerCapture?.(event.pointerId);
      setDrag({ activeId: id, order: [...ids], offsetX: 0, offsetY: 0 });
      if (layout === 'grid') scrollFrame.current = window.requestAnimationFrame(autoScroll);
    };
    if (layout === 'grid' && event.pointerType === 'touch') hold.current = window.setTimeout(activate, 300);
    else activate();
  }, [ids, layout, autoScroll]);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const current = session.current;
      if (!current || current.pointerId !== event.pointerId) return;
      current.x = event.clientX;
      current.y = event.clientY;
      if (!current.active) {
        if (Math.hypot(current.x - current.startX, current.y - current.startY) > 8) cancel();
        return;
      }
      event.preventDefault();
      update();
    };
    const finish = (event: PointerEvent) => {
      const current = session.current;
      if (!current || current.pointerId !== event.pointerId) return;
      const changed = current.active && current.order.some((id, index) => id !== current.original[index]);
      cancel();
      if (changed) commit.current(current.order);
    };
    const pointerCancel = (event: PointerEvent) => {
      if (session.current?.pointerId === event.pointerId) cancel();
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') cancel(); };
    const visibility = () => { if (document.visibilityState !== 'visible') cancel(); };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', pointerCancel);
    window.addEventListener('keydown', escape);
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', pointerCancel);
      window.removeEventListener('keydown', escape);
      window.removeEventListener('blur', cancel);
      document.removeEventListener('visibilitychange', visibility);
      cancel();
    };
  }, [cancel, update]);

  const membership = [...ids].sort().join(':');
  useEffect(() => {
    const current = session.current;
    if (current && [...current.original].sort().join(':') !== membership) cancel();
  }, [membership, cancel]);

  return {
    order: drag?.order ?? ids,
    activeId: drag?.activeId ?? null,
    offsetY: drag?.offsetY ?? 0,
    offsetX: drag?.offsetX ?? 0,
    isDragging: drag !== null,
    beginDrag,
    registerItem,
  };
}
